import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import InteractionHints from './InteractionHints';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const NORM = 125;
const DEFAULT_CAM = { x: 0, y: 1.36, z: 1.62 };

/**
 * Build a T-shaped 2D profile for extrusion.
 * a = long width (A), b = full depth (B), c = back width (C), d = short side depth (D)
 * All values are in NORM-scaled scene units.
 *
 * Shape layout (top-down, y-axis is depth):
 *   Front edge at y = -halfB, width A
 *   Step transition at y = -halfB + d
 *   Back edge at y = +halfB, width C
 */
function createTShape(a, b, c, d) {
  const halfA = a / 2, halfC = c / 2, halfB = b / 2;
  const stepW = (a - c) / 2;
  const wingDepth = Math.max(0.01, b - d);  // wing = total depth minus longer side depth
  const stepY = -halfB + wingDepth;
  const r = Math.max(0.0005, Math.min(2 / NORM, stepW * 0.25, wingDepth * 0.25, d * 0.25));

  const s = new THREE.Shape();
  // Start bottom-left, traverse counter-clockwise (Three.js convention for outer path)
  s.moveTo(-halfA + r, -halfB);
  s.lineTo(halfA - r, -halfB);
  s.absarc(halfA - r, -halfB + r, r, -Math.PI / 2, 0, false);
  s.lineTo(halfA, stepY - r);
  s.absarc(halfA - r, stepY - r, r, 0, Math.PI / 2, false);
  s.lineTo(halfC + r, stepY);
  s.absarc(halfC + r, stepY + r, r, -Math.PI / 2, Math.PI, true);
  s.lineTo(halfC, halfB - r);
  s.absarc(halfC - r, halfB - r, r, 0, Math.PI / 2, false);
  s.lineTo(-halfC + r, halfB);
  s.absarc(-halfC + r, halfB - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-halfC, stepY + r);
  s.absarc(-halfC - r, stepY + r, r, 0, -Math.PI / 2, true);
  s.lineTo(-halfA + r, stepY);
  s.absarc(-halfA + r, stepY - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-halfA, -halfB + r);
  s.absarc(-halfA + r, -halfB + r, r, Math.PI, 3 * Math.PI / 2, false);
  return s;
}

const TCushion3DPreview = ({
  longWidth = 90, fullDepth = 80, backWidth = 70, shortSideDepth = 60, thickness = 15, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 3, showZip = true,
  rawA = 0, rawB = 0, rawC = 0, rawD = 0, unit = 'cm',
  activeField = null,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const mountRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cushionRef = useRef(null);
  const zipRef = useRef(null);
  const dimLinesRef = useRef(null);
  const dimLabelsRef = useRef({ a: null, b: null, c: null, d: null, t: null });
  const currentDimsRef = useRef(null);
  const containerSizeRef = useRef({ w: 0, h: 0 });
  const initializedRef = useRef(false);
  const animFrameRef = useRef(null);
  const targetDimsRef = useRef(null);
  const resetCameraRef = useRef(true);
  const showZipRef = useRef(showZip);
  const activeFieldRef = useRef(activeField);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);

  useEffect(() => { showZipRef.current = showZip; }, [showZip]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  // === Scene initialization (runs once) ===
  useEffect(() => {
    if (!mountRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = mountRef.current;
    const w = container.clientWidth, h = container.clientHeight;
    containerSizeRef.current = { w, h };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f0eb);

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };

    // Lighting (matched to Bay Window)
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(3, 5, 3);
    dir.castShadow = true;
    dir.shadow.mapSize.set(1024, 1024);
    scene.add(dir);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Floor shadow
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(10, 10),
      new THREE.ShadowMaterial({ opacity: 0.1 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Initial cushion geometry
    const a0 = longWidth / NORM, b0 = fullDepth / NORM;
    const c0 = backWidth / NORM, d0 = shortSideDepth / NORM, t0 = thickness / NORM;
    const shape = createTShape(a0, b0, c0, d0);
    const bevel = Math.min(t0 * 0.06, 0.003);
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: t0, bevelEnabled: true, bevelThickness: bevel,
      bevelSize: bevel, bevelSegments: 3, curveSegments: 48
    });
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, t0 / 2, 0);
    remapExtrudeUVs(geo);
    geo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({ color: 0x5a8a5a, roughness: 0.85, metalness: 0.02 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    scene.add(mesh);
    cushionRef.current = mesh;
    currentDimsRef.current = { a: a0, b: b0, c: c0, d: d0, t: t0 };
    targetDimsRef.current = { a: a0, b: b0, c: c0, d: d0, t: t0 };

    // Zip: U-shaped (full back edge + halfway down both D sides)
    const trackMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipBack = new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), trackMat);
    const zipLeft = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    const zipRight = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 1), trackMat);
    scene.add(zipBack, zipLeft, zipRight);

    const toothMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipTeeth = Array.from({ length: 80 }, () => {
      const t = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.006, 0.008), toothMat);
      scene.add(t); return t;
    });

    const zipPull = new THREE.Mesh(
      new THREE.BoxGeometry(0.03, 0.005, 0.006),
      new THREE.MeshBasicMaterial({ color: 0xf5f5f5 })
    );
    scene.add(zipPull);

    zipRef.current = { back: zipBack, left: zipLeft, right: zipRight, teeth: zipTeeth, pull: zipPull };

    // Dimension lines with per-dimension materials for highlight
    const aMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const bMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const cMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const arrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    // A dashes (width direction, along x-axis near front edge)
    const aDashes = Array.from({ length: 28 }, () => {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), aMat);
      scene.add(d); return d;
    });
    const aA1 = new THREE.Mesh(arrowGeo, aMat); scene.add(aA1);
    const aA2 = new THREE.Mesh(arrowGeo, aMat); scene.add(aA2);

    // B dashes (depth direction, along z-axis)
    const bDashes = Array.from({ length: 28 }, () => {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), bMat);
      scene.add(d); return d;
    });
    const bA1 = new THREE.Mesh(arrowGeo, bMat); scene.add(bA1);
    const bA2 = new THREE.Mesh(arrowGeo, bMat); scene.add(bA2);

    // C dashes (width direction, along x-axis)
    const cDashes = Array.from({ length: 20 }, () => {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), cMat);
      scene.add(d); return d;
    });
    const cA1 = new THREE.Mesh(arrowGeo, cMat); scene.add(cA1);
    const cA2 = new THREE.Mesh(arrowGeo, cMat); scene.add(cA2);

    // D dashes (side depth, along z-axis on right edge)
    const dDashes = Array.from({ length: 22 }, () => {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), dMat);
      scene.add(d); return d;
    });
    const dA1 = new THREE.Mesh(arrowGeo, dMat); scene.add(dA1);
    const dA2 = new THREE.Mesh(arrowGeo, dMat); scene.add(dA2);

    dimLinesRef.current = { aDashes, aA1, aA2, bDashes, bA1, bA2, cDashes, cA1, cA2, dDashes, dA1, dA2, aMat, bMat, cMat, dMat };

    // Piping rings (top + bottom)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;

    const targetCamPos = new THREE.Vector3();

    // === Animation loop ===
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = dimLinesRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });
        const __zr = zipRef.current;
        if (__zr) {
          if (__zr.track) __zr.track.visible = false;
          if (__zr.teeth) __zr.teeth.forEach(t => t.visible = false);
          if (__zr.pull) __zr.pull.visible = false;
        }
      }
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      // Smooth dimension interpolation
      let needsRebuild = false;
      const next = {};
      for (const key of ['a', 'b', 'c', 'd', 't']) {
        next[key] = THREE.MathUtils.lerp(cd[key], td[key], 0.12);
        if (Math.abs(next[key] - td[key]) > 0.0001) needsRebuild = true;
      }

      if (needsRebuild) {
        mesh.geometry.dispose();
        const shape = createTShape(next.a, next.b, next.c, next.d);
        const bev = Math.min(next.t * 0.06, 0.003);
        const geo = new THREE.ExtrudeGeometry(shape, {
          depth: next.t, bevelEnabled: true, bevelThickness: bev,
          bevelSize: bev, bevelSegments: 3, curveSegments: 32
        });
        geo.rotateX(-Math.PI / 2);
        geo.translate(0, next.t / 2, 0);
        remapExtrudeUVs(geo);
        geo.computeVertexNormals();
        mesh.geometry = geo;
        currentDimsRef.current = next;
      } else if (needsRebuild === false) {
        // Snap to target when close enough
        currentDimsRef.current = { ...td };
      }

      // Floor tracks cushion bottom
      floor.position.y = -cd.t / 2 - 0.002;

      // Track piping Y with lerped thickness
      if (pipingTopRef.current && pipingTopRef.current.visible) {
        const pBev = Math.min(cd.t * 0.06, 0.003);
        pipingTopRef.current.position.y = cd.t * 1.5 + pBev * Math.SQRT1_2;
        pipingBottomRef.current.position.y = cd.t * 0.5 - pBev * Math.SQRT1_2;
      }

      // Zip: U-shaped — back edge + halfway down both D sides
      const zr = zipRef.current;
      const isVis = showZipRef.current;
      const halfB_z = cd.b / 2;
      const halfC_z = cd.c / 2;
      const armLen = cd.d / 2;

      // Back track: full C width
      zr.back.visible = isVis;
      zr.back.scale.x = cd.c;
      zr.back.position.set(0, cd.t, -halfB_z - 0.001);

      // Left arm: half D length, going forward from back-left corner
      zr.left.visible = isVis;
      zr.left.scale.z = armLen;
      zr.left.position.set(-halfC_z, cd.t, -halfB_z + armLen / 2);

      // Right arm: half D length, going forward from back-right corner
      zr.right.visible = isVis;
      zr.right.scale.z = armLen;
      zr.right.position.set(halfC_z, cd.t, -halfB_z + armLen / 2);

      // Pull tab at end of right arm
      zr.pull.visible = isVis;
      zr.pull.position.set(halfC_z, cd.t + 0.004, -halfB_z + armLen + 0.008);

      // Teeth along U-path: right arm → back → left arm
      const totalLen = cd.c + cd.d;
      for (let i = 0; i < zr.teeth.length; i++) {
        const tooth = zr.teeth[i];
        tooth.visible = isVis;
        const param = (i + 0.5) / zr.teeth.length * totalLen;
        const alt = (i % 2 === 0 ? 1 : -1) * 0.010;

        if (param < armLen) {
          // Right arm: along z, teeth alternate in x
          tooth.position.set(halfC_z + alt, cd.t, (-halfB_z + armLen) - param);
        } else if (param < armLen + cd.c) {
          // Back: along x, teeth alternate in z
          tooth.position.set(halfC_z - (param - armLen), cd.t, -halfB_z + alt);
        } else {
          // Left arm: along z, teeth alternate in x
          tooth.position.set(-halfC_z + alt, cd.t, -halfB_z + (param - armLen - cd.c));
        }
      }

      // Dimension lines positioning
      const dl = dimLinesRef.current;
      if (dl) {
        const bev = Math.min(cd.t * 0.06, 0.003);
        const topY = cd.t * 1.5 + bev + 0.004;
        const halfA = cd.a / 2;
        const halfB = cd.b / 2;
        const halfC = cd.c / 2;

        // A line: long width, runs left-to-right, slightly inside from front edge
        const aZ = halfB - 0.06;
        for (let i = 0; i < dl.aDashes.length; i++) {
          const t2 = (i + 0.5) / dl.aDashes.length;
          dl.aDashes[i].position.set(-halfA + cd.a * t2, topY, aZ);
        }
        dl.aA1.position.set(-halfA, topY, aZ);
        dl.aA1.rotation.set(0, 0, Math.PI / 2);    // arrow points towards -x (left)
        dl.aA2.position.set(halfA, topY, aZ);
        dl.aA2.rotation.set(0, 0, -Math.PI / 2);   // arrow points towards +x (right)

        // B line: full depth, runs front-to-back, exact center of cushion on top
        const bX = 0;
        for (let i = 0; i < dl.bDashes.length; i++) {
          const t2 = (i + 0.5) / dl.bDashes.length;
          dl.bDashes[i].position.set(bX, topY, halfB - cd.b * t2);
        }
        dl.bA1.position.set(bX, topY, halfB);
        dl.bA1.rotation.set(Math.PI / 2, 0, 0);   // arrow points towards +z (front)
        dl.bA2.position.set(bX, topY, -halfB);
        dl.bA2.rotation.set(-Math.PI / 2, 0, 0);  // arrow points towards -z (back)

        // C line: back width, runs left-to-right, on top further inside from back edge
        const cZ = -halfB + 0.08;
        for (let i = 0; i < dl.cDashes.length; i++) {
          const t2 = (i + 0.5) / dl.cDashes.length;
          dl.cDashes[i].position.set(-halfC + cd.c * t2, topY, cZ);
        }
        dl.cA1.position.set(-halfC, topY, cZ);
        dl.cA1.rotation.set(0, 0, Math.PI / 2);    // arrow points towards -x (left)
        dl.cA2.position.set(halfC, topY, cZ);
        dl.cA2.rotation.set(0, 0, -Math.PI / 2);   // arrow points towards +x (right)

        // D line: side depth, runs back-to-step on right edge inside cushion
        const wingDepth = cd.b - cd.d;
        const dX = halfC - 0.04;  // slightly inside from right edge
        const dZBack = -halfB;          // back edge
        const dZStep = -halfB + cd.d;   // step transition
        for (let i = 0; i < dl.dDashes.length; i++) {
          const t2 = (i + 0.5) / dl.dDashes.length;
          dl.dDashes[i].position.set(dX, topY, dZBack + cd.d * t2);
        }
        dl.dA1.position.set(dX, topY, dZBack);
        dl.dA1.rotation.set(-Math.PI / 2, 0, 0);   // arrow points towards -z (back)
        dl.dA2.position.set(dX, topY, dZStep);
        dl.dA2.rotation.set(Math.PI / 2, 0, 0);    // arrow points towards +z (step)
      }

      // Project label positions to 2D + highlight active field
      const { w: cw, h: ch } = containerSizeRef.current;
      const bev2 = Math.min(cd.t * 0.06, 0.003);
      const labelY = cd.t * 1.5 + bev2 + 0.004;
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      const WHITE = 0xffffff;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      // Update line/arrow colors
      if (dl) {
        dl.aMat.color.set(af === 'a' ? YELLOW : WHITE);
        dl.bMat.color.set(af === 'b' ? YELLOW : WHITE);
        dl.cMat.color.set(af === 'c' ? YELLOW : WHITE);
        dl.dMat.color.set(af === 'd' ? YELLOW : WHITE);
      }

      // A label: front edge, left side near the A line
      if (dimLabelsRef.current.a) {
        const v = new THREE.Vector3(-(cd.a / 3), labelY, cd.b / 2 - 0.03);
        v.project(camera);
        dimLabelsRef.current.a.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
        dimLabelsRef.current.a.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
        dimLabelsRef.current.a.style.opacity = '1';
        dimLabelsRef.current.a.style.backgroundColor = af === 'a' ? activeBg : defaultBg;
        dimLabelsRef.current.a.style.color = af === 'a' ? activeText : defaultText;
      }
      // B label: next to center line, slight offset
      if (dimLabelsRef.current.b) {
        const v = new THREE.Vector3(0.04, labelY, 0);
        v.project(camera);
        dimLabelsRef.current.b.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
        dimLabelsRef.current.b.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
        dimLabelsRef.current.b.style.opacity = '1';
        dimLabelsRef.current.b.style.backgroundColor = af === 'b' ? activeBg : defaultBg;
        dimLabelsRef.current.b.style.color = af === 'b' ? activeText : defaultText;
      }
      // C label: near left edge of C line, away from B center line
      if (dimLabelsRef.current.c) {
        const v = new THREE.Vector3(-(cd.c / 4), labelY, -(cd.b / 2) + 0.12);
        v.project(camera);
        dimLabelsRef.current.c.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
        dimLabelsRef.current.c.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
        dimLabelsRef.current.c.style.opacity = '1';
        dimLabelsRef.current.c.style.backgroundColor = af === 'c' ? activeBg : defaultBg;
        dimLabelsRef.current.c.style.color = af === 'c' ? activeText : defaultText;
      }
      // D label: right side, midpoint of D line
      if (dimLabelsRef.current.d) {
        const dMidZ = -(cd.b / 2) + cd.d / 2;
        const v = new THREE.Vector3(cd.c / 2 - 0.01, labelY, dMidZ);
        v.project(camera);
        dimLabelsRef.current.d.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
        dimLabelsRef.current.d.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
        dimLabelsRef.current.d.style.opacity = '1';
        dimLabelsRef.current.d.style.backgroundColor = af === 'd' ? activeBg : defaultBg;
        dimLabelsRef.current.d.style.color = af === 'd' ? activeText : defaultText;
      }
      // Thickness label: right side of cushion, halfway up thickness
      if (dimLabelsRef.current.t) {
        const v = new THREE.Vector3(cd.a / 2 + 0.03, cd.t, cd.b / 2);
        v.project(camera);
        dimLabelsRef.current.t.style.left = `${(v.x * 0.5 + 0.5) * cw}px`;
        dimLabelsRef.current.t.style.top = `${(-v.y * 0.5 + 0.5) * ch}px`;
        dimLabelsRef.current.t.style.opacity = '1';
        dimLabelsRef.current.t.style.backgroundColor = af === 't' ? activeBg : defaultBg;
        dimLabelsRef.current.t.style.color = af === 't' ? activeText : defaultText;
      }

      // Camera auto-position (only when reset requested)
      if (resetCameraRef.current) {
        const maxDim = Math.max(cd.a, cd.b);
        const isMobile = cw < 500;
        const distMult = isMobile ? 0.5 : 1.04;
        const dist = maxDim * distMult + 0.22;
        const camScale = isMobile ? 1.0 : 1.7;
        targetCamPos.set(
          DEFAULT_CAM.x / camScale * dist,
          DEFAULT_CAM.y / camScale * dist,
          DEFAULT_CAM.z / camScale * dist
        );
        camera.position.lerp(targetCamPos, 0.1);
        controls.target.set(0, cd.t / 4, 0);
        if (camera.position.distanceTo(targetCamPos) < 0.01) {
          resetCameraRef.current = false;
          controls.saveState();
        }
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      containerSizeRef.current = { w: nw, h: nh };
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  // === Update target dimensions (animate loop will smoothly interpolate) ===
  useEffect(() => {
    if (!targetDimsRef.current) return;
    const a = longWidth / NORM, b = fullDepth / NORM;
    const c = backWidth / NORM, d = shortSideDepth / NORM, t = thickness / NORM;
    targetDimsRef.current = { a, b, c, d, t };
    resetCameraRef.current = true;
  }, [longWidth, fullDepth, backWidth, shortSideDepth, thickness]);

  // === Update material ===
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, fabricColor, fabricZoom, () => setFabricLoading(false));
    }
  }, [fabricColor, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings (top + bottom, T-shape perimeter)
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const a = longWidth / NORM, b = fullDepth / NORM;
    const c = backWidth / NORM, d = shortSideDepth / NORM, t = thickness / NORM;
    const bevel = Math.min(t * 0.06, 0.003);
    const tubeRadius = 0.003;
    const expansion = bevel * Math.SQRT1_2;

    // Trace T-shape perimeter using createTShape
    const shape = createTShape(a, b, c, d);
    const pts2D = shape.getPoints(128);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    // Convert to XZ plane: shape X→world X, shape Y→world -Z
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x, 0, -p.y));

    // Expand outward
    const n = pts3D.length;
    const expanded = pts3D.map((pt3, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next2 = pts3D[(i + 1) % n];
      const dx = next2.x - prev.x;
      const dz = next2.z - prev.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const dot = nx * pt3.x + nz * pt3.z;
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(pt3.x + sign * nx * expansion, 0, pt3.z + sign * nz * expansion);
    });

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t2) {
        const n2 = this.pts.length;
        const f = t2 * n2;
        const i = Math.floor(f) % n2;
        const a2 = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a2);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 192, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    pt.position.y = t * 1.5 + expansion;
    pb.position.y = t * 0.5 - expansion;
  }, [longWidth, fullDepth, backWidth, shortSideDepth, thickness, fabricColor, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="t-cushion-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.a = el} data-testid="dim-label-a" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawA > 0 ? `${unit === 'mm' ? Math.round(rawA * 10) : rawA} ${unit}` : `A ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.b = el} data-testid="dim-label-b" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawB > 0 ? `${unit === 'mm' ? Math.round(rawB * 10) : rawB} ${unit}` : `B ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.c = el} data-testid="dim-label-c" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawC > 0 ? `${unit === 'mm' ? Math.round(rawC * 10) : rawC} ${unit}` : `C ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.d = el} data-testid="dim-label-d" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawD > 0 ? `${unit === 'mm' ? Math.round(rawD * 10) : rawD} ${unit}` : `D ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.t = el} data-testid="dim-label-t" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>
        {rawThickness ? `${unit === 'mm' ? Math.round(rawThickness * 10) : rawThickness} ${unit}` : `T ${unit}`}
      </div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip on back edge</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default TCushion3DPreview;
